import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import i18n from '@/utils/i18n';

export type TablePaginationProps = {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  disabled?: boolean;
};

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  disabled = false,
}) => {
  const canGoPrevious = currentPage > 1 && !disabled;
  const canGoNext = currentPage < totalPages && !disabled;

  return (
    <div className="tw:flex tw:items-center tw:justify-end tw:gap-[21px]">
      {/* Rows per page selector */}
      <div className="tw:flex tw:items-center tw:gap-3">
        <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
          {i18n.t('Common:rowsPerPage') || 'Rows per page'}
        </p>
        <Select
          value={rowsPerPage.toString()}
          onValueChange={(value) => onRowsPerPageChange(Number(value))}
          disabled={disabled}
        >
          <SelectTrigger
            className="tw:h-8 tw:w-[70px] tw:bg-neutral-100 tw:border-0 tw:rounded-lg"
            disabled={disabled}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rowsPerPageOptions.map((option) => (
              <SelectItem key={option} value={option.toString()}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Page indicator */}
      <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
        {i18n.t('Common:page') || 'Page'} {currentPage} / {totalPages}
      </p>

      {/* Navigation buttons */}
      <div className="tw:flex tw:items-center tw:gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrevious}
          className="tw:bg-neutral-100 tw:flex tw:items-center tw:justify-center tw:min-h-[32px] tw:min-w-[32px] tw:p-[7px] tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:hover:bg-neutral-200 tw:disabled:opacity-50 tw:disabled:cursor-not-allowed tw:disabled:hover:bg-neutral-100"
          aria-label="Previous page"
        >
          <ChevronLeft className="tw:size-[14.75px] tw:text-neutral-900" />
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext}
          className="tw:bg-neutral-100 tw:flex tw:items-center tw:justify-center tw:min-h-[32px] tw:min-w-[32px] tw:p-[7px] tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:hover:bg-neutral-200 tw:disabled:opacity-50 tw:disabled:cursor-not-allowed tw:disabled:hover:bg-neutral-100"
          aria-label="Next page"
        >
          <ChevronRight className="tw:size-[14.75px] tw:text-neutral-900" />
        </button>
      </div>
    </div>
  );
};
